import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil, Trash2, Users, Clock, Scissors } from 'lucide-react';
import api from '../../lib/axios';
import PageHeader    from '../../components/ui/PageHeader';
import EmptyState    from '../../components/ui/EmptyState';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import StaffEditModal        from './StaffEditModal';
import WorkingHoursModal     from './WorkingHoursModal';
import StaffServicesModal    from './StaffServicesModal';

export default function Staff() {
  const qc = useQueryClient();
  const [editTarget,    setEditTarget]    = useState(null);
  const [hoursTarget,   setHoursTarget]   = useState(null);
  const [servicesTarget, setServicesTarget] = useState(null);
  const [deleteTarget,  setDeleteTarget]  = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['staff'],
    queryFn:  () => api.get('/staff').then(r => r.data.data),
  });

  const deleteMut = useMutation({
    mutationFn: (id) => api.delete(`/staff/${id}`),
    onSuccess:  () => { qc.invalidateQueries(['staff']); setDeleteTarget(null); },
  });

  const members = data || [];

  return (
    <div className="p-4 sm:p-6 max-w-5xl">
      <PageHeader
        title="Staff"
        subtitle={`${members.length} team member${members.length !== 1 ? 's' : ''}`}
        action={
          <button onClick={() => setEditTarget({})} className="btn-primary">
            <Plus size={16} /> Add staff
          </button>
        }
      />

      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1,2,3].map(i => <div key={i} className="card h-40 animate-pulse bg-surface-100" />)}
        </div>
      ) : members.length === 0 ? (
        <EmptyState
          icon={Users}
          title="No staff members yet"
          description="Add your team so clients can book with their preferred provider."
          action={<button onClick={() => setEditTarget({})} className="btn-primary">Add staff member</button>}
        />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {members.map(member => (
            <div key={member.id} className="card p-5 flex flex-col gap-4">
              {/* Avatar + name */}
              <div className="flex items-start gap-3">
                <div className="w-11 h-11 rounded-full flex items-center justify-center text-white font-semibold text-base shrink-0"
                  style={{ background: member.color }}>
                  {member.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-ink truncate">{member.name}</p>
                    {!member.active && <span className="badge badge-gray">Inactive</span>}
                  </div>
                  <p className="text-xs text-ink-muted truncate">{member.email || '—'}</p>
                </div>
              </div>

              {/* Stats */}
              <div className="flex gap-3 text-xs text-ink-secondary">
                <span className="flex items-center gap-1">
                  <Scissors size={12} />
                  {(member.Services || []).length} service{(member.Services || []).length !== 1 ? 's' : ''}
                </span>
                <span className="flex items-center gap-1">
                  <Clock size={12} />
                  {(member.WorkingHours || []).length} day{(member.WorkingHours || []).length !== 1 ? 's' : ''}
                </span>
              </div>

              {/* Services pills */}
              {(member.Services || []).length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {member.Services.slice(0, 3).map(s => (
                    <span key={s.id} className="badge badge-blue text-xs">{s.name}</span>
                  ))}
                  {member.Services.length > 3 && (
                    <span className="badge badge-gray">+{member.Services.length - 3}</span>
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center gap-1 mt-auto pt-2 border-t border-surface-border">
                <button onClick={() => setEditTarget(member)} className="btn-ghost flex-1 text-xs justify-center py-1.5 min-w-0">
                  <Pencil size={13} /> <span className="truncate">Edit</span>
                </button>
                <button onClick={() => setHoursTarget(member)} className="btn-ghost flex-1 text-xs justify-center py-1.5 min-w-0">
                  <Clock size={13} /> <span className="truncate">Hours</span>
                </button>
                <button onClick={() => setServicesTarget(member)} className="btn-ghost flex-1 text-xs justify-center py-1.5 min-w-0">
                  <Scissors size={13} /> <span className="truncate">Services</span>
                </button>
                <button onClick={() => setDeleteTarget(member)} className="btn-ghost p-1.5 shrink-0 text-red-500 hover:bg-red-50">
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <StaffEditModal     open={editTarget !== null}     member={editTarget}    onClose={() => setEditTarget(null)} />
      <WorkingHoursModal  open={hoursTarget !== null}    member={hoursTarget}   onClose={() => setHoursTarget(null)} />
      <StaffServicesModal open={servicesTarget !== null} member={servicesTarget} onClose={() => setServicesTarget(null)} />

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteMut.mutate(deleteTarget?.id)}
        loading={deleteMut.isPending}
        title="Remove staff member?"
        message={`${deleteTarget?.name} will be removed. Their past bookings are not affected.`}
      />
    </div>
  );
}
